import routes from '@/config/routes';

import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import type { ApiResponse } from '@/api/types';
import type { ISetEquipmentAnswer, IWorkHistory } from '@/store/types';
import type {
  IContact,
  IEducation,
  IProfilePicturePayload,
  ISetProficiency,
  IStepStatus,
  ISpeedTestResult,
  IApplicationPayload,
} from '@/types/resume-builder.model';

import { v3Get, v3Patch, v3Post, v3Put, v3Remove } from './axios.instance.V3';

const prefixMember = '/member';

const url = {
  getEquipments: (application_id: number) =>
    `${routes.backendUrl.equipment.getEquipments}/${application_id}?d=true&c=equipmentId`,
  getEquipmentAnswersList: () => `${routes.backendUrl.equipment.getEquipmentAnswersList}`,
  getEquipmentAnswers: () => `${routes.backendUrl.equipment.getEquipmentAnswersUser}`,
  setEquipmentAnswers: () => `${routes.backendUrl.equipment.setEquipmentAnswers}`,
  getInternetTestHistory: (userId: undefined | number) => {
    return userId ? `/su/member/profile/speedtest/list/${userId}` : `${routes.resumeBuilderWizard.internetTestList}`;
  },
  getApplicationInternetTest: (applicationId: number) =>
    `${routes.resumeBuilderWizard.applicationInternetTest}`.replace('{applicationId}', applicationId.toString()),
  addInternetTestResult: () => `${routes.resumeBuilderWizard.internetTestCreate}`,
  getTestStatus: (requirementType: number, userId: undefined | number) => {
    return userId
      ? `/su/member/gigs/applied/status/list/${userId}/${requirementType}`
      : `${routes.resumeBuilderWizard.testStatus}/${requirementType}`;
  },
  getWorkHistoryList: () => `${prefixMember}/workhistory/g/list/user`,
  setWorkHistory: () => `${prefixMember}/workhistory/g/create`,
  deleteWorkHistory: (workHistoryId: number) => `${prefixMember}/workhistory/${workHistoryId}`,
  updateWorkHistory: () => `${prefixMember}/workhistory/g/update`,
  getEducationOptions: () => `${routes.resumeBuilderWizard.educationOptions}`,
  getLanguageOptions: () => `${routes.resumeBuilderWizard.languageOptions}`,
  getProficiencyAreaOptions: () => `${routes.resumeBuilderWizard.proficiencyAreaOptions}`,
  getProficiencyOptions: () => `${routes.resumeBuilderWizard.proficiencyOptions}`,

  setLanguageProficiencies: () => `${routes.resumeBuilderWizard.setLanguageProficiencies}`,
  getMemberSkills: () => `${routes.resumeBuilderWizard.getMemberSkills}`,
  setMemberSkills: () => `${routes.resumeBuilderWizard.setMemberSkills}`,
} as const;

const getEquipmentsList = (application_id: number): ApiResponse => v3Get(url.getEquipments(application_id));
const getEquipmentsAnswersList = (): ApiResponse => v3Get(url.getEquipmentAnswersList());
const getEquipmentAnswers = (): ApiResponse => v3Get(url.getEquipmentAnswers());
const getWorkHistoryList = (): ApiResponse => v3Get(url.getWorkHistoryList());
const setWorkHistory = (payload: IWorkHistory): ApiResponse => v3Post(url.setWorkHistory(), payload);
const deleteWorkHistory = (workHistoryId: number): ApiResponse => v3Remove(url.deleteWorkHistory(workHistoryId));
const updateWorkHistory = (payload: IWorkHistory): ApiResponse => v3Put(url.updateWorkHistory(), payload);
const getEducationOptions = (): ApiResponse => v3Get(url.getEducationOptions());
const getLanguageOptions = (): ApiResponse => v3Get(url.getLanguageOptions());
const getProficiencyAreaOptions = (): ApiResponse => v3Get(url.getProficiencyAreaOptions());
const getProficiencyOptions = (): ApiResponse => v3Get(url.getProficiencyOptions());

const getHardwareList = (userId: undefined | number): ApiResponse => {
  return v3Get(userId ? `/su/member/hardwaretest/list/user/${userId}` : `${routes.backendUrl.hardware.getList}`);
};
const getHardwareDetail = (testid: number): ApiResponse => v3Get(`${routes.backendUrl.hardware.getDetails}/${testid}`);
const getHardwareTestToken = (): ApiResponse => v3Get(routes.backendUrl.hardware.getToken);
const getVoiceQuestionList = (gigid: number): ApiResponse =>
  v3Get(`${routes.backendUrl.voice.getVoiceQuestion}/${gigid}?d=true`);
const getVoiceAnswers = (applicationId: number): ApiResponse =>
  v3Get(`${routes.backendUrl.voice.getVoiceAnswer}/${applicationId}`);
const getStepStatus = async (applicationId: number): ApiResponse =>
  v3Get(`${routes.backendUrl.gigApplication.getStepStatus}/${applicationId}?d=true&c=stepStatusId`);
const getContactInformation = (): ApiResponse => v3Get(routes.backendUrl.getContactDetails);
const getProfilePicture = (): ApiResponse => v3Get(`${routes.resumeBuilderWizard.getProfilePicture}`);
const getApplicationFlags = (applicationId: number): ApiResponse =>
  v3Get(`${routes.backendUrl.gigApplication.getApplicationFlags}/${applicationId}`);
const getTestStatus = (requirementType: number, userId: undefined | number): ApiResponse =>
  v3Get(url.getTestStatus(requirementType, userId));
const setApplicationFlags = async (applicationPayload: IApplicationPayload): ApiResponse => {
  return await v3Post(`${routes.backendUrl.gigApplication.saveApplicationFlags}`, applicationPayload);
};
const setContactInformation = async (payload: IContact): ApiResponse => {
  return await v3Patch(routes.backendUrl.setContactDetails, payload);
};

const setStepStatus = async (stepStatus: IStepStatus): ApiResponse => {
  const payload = {
    applicationId: stepStatus.applicationId,
    stepId: stepStatus.stepId,
    stepStatusId: stepStatus.stepStatusId,
    application: {
      isSupportedDevice: stepStatus.isSupportedDevice,
    },
  };
  return await v3Post(`${routes.backendUrl.gigApplication.saveStepStatus}`, payload);
};
const setProfilePicture = async (photoPayload: IProfilePicturePayload): Promise<AxiosResponse> => {
  const fileData = new FormData();
  fileData.append('File', photoPayload.file);
  const config: AxiosRequestConfig = {
    headers: {
      'Content-Type': 'multipart/form-data',
      'Access-Control-Allow-Origin': '*',
    },
  };
  return await v3Post(`${routes.backendUrl.member.profilePhoto.save}/${photoPayload.id}`, fileData, config);
};

const setVoiceAnswer = async (payload: any, applicationId: number, voiceQuestionId: number): Promise<AxiosResponse> => {
  const fileData = new FormData();
  fileData.append('file', payload);
  const config: AxiosRequestConfig = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'Access-Control-Allow-Origin': '*',
    },
  };
  return await v3Post(`/member/voiceanswer/${applicationId}​​​/${voiceQuestionId}/save`, fileData, config);
};

const setEquipmentAnswers = async (payload: ISetEquipmentAnswer[]): Promise<AxiosResponse> => {
  return await v3Post(`${url.setEquipmentAnswers()}`, payload);
};

const setLanguageProficiencies = async (payload: ISetProficiency[]): Promise<AxiosResponse> => {
  return await v3Post(`${url.setLanguageProficiencies()}`, payload);
};

const getMemberSkills = (): ApiResponse => v3Get(url.getMemberSkills());

const setMemberSkills = async (payload: IEducation): Promise<AxiosResponse> => {
  return await v3Post(`${url.setMemberSkills()}`, payload);
};

const getTestResultDetails = (testId: number): ApiResponse =>
  v3Get(`${routes.resumeBuilderWizard.internetTestDetails}/${testId}`);

const getInternetTestHistory = (userId: undefined | number): ApiResponse => v3Get(url.getInternetTestHistory(userId));

const getApplicationInternetTest = (applicationId: number): ApiResponse =>
  v3Get(url.getApplicationInternetTest(applicationId));

const addInternetTestResult = (speedTestResult: ISpeedTestResult): ApiResponse =>
  v3Post(`${url.addInternetTestResult()}`, speedTestResult);

export const resumeBuilderApis = {
  getEquipmentsList,
  getEquipmentsAnswersList,
  getEquipmentAnswers,
  setEquipmentAnswers,
  getInternetTestHistory,
  getTestStatus,
  getTestResultDetails,
  getWorkHistoryList,
  setWorkHistory,
  deleteWorkHistory,
  updateWorkHistory,
  getEducationOptions,
  getLanguageOptions,
  getProficiencyAreaOptions,
  getProficiencyOptions,
  setLanguageProficiencies,
  getMemberSkills,
  setMemberSkills,
  getHardwareList,
  getHardwareTestToken,
  getHardwareDetail,
  getVoiceQuestionList,
  setVoiceAnswer,
  getVoiceAnswers,
  setStepStatus,
  getStepStatus,
  getContactInformation,
  setContactInformation,
  setProfilePicture,
  getProfilePicture,
  setApplicationFlags,
  getApplicationFlags,
  addInternetTestResult,
  getApplicationInternetTest,
};
